import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface UserRolesData {
  isAdmin: boolean;
  isDeveloper: boolean;
  isSuperAdmin: boolean;
  isTeacher: boolean;
  isStaff: boolean;
  loading: boolean;
}

const EMPTY = {
  isAdmin: false,
  isDeveloper: false,
  isSuperAdmin: false,
  isTeacher: false,
};

/**
 * Fonte única de verdade para os papéis (roles) do usuário corrente.
 *
 * Consolida `is_admin`, `is_developer`, `is_super_admin` e `is_teacher_approved`
 * em uma única query React Query (`['user-roles', userId]`). Hooks legados
 * (`useUserRoles`, `useIsSuperAdmin`, `useStaffBypass`) reaproveitam esse cache.
 */
export function useRoles(): UserRolesData {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['user-roles', user?.id],
    queryFn: async () => {
      if (!user) return EMPTY;
      const [adminRes, devRes, superRes, teacherRes] = await Promise.all([
        supabase.rpc('is_admin' as any),
        supabase.rpc('is_developer' as any),
        supabase.rpc('is_super_admin'),
        supabase.rpc('is_teacher_approved' as any),
      ]);
      return {
        isAdmin: adminRes.data === true,
        isDeveloper: devRes.data === true,
        isSuperAdmin: superRes.data === true,
        isTeacher: teacherRes.data === true,
      };
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });

  const r = data ?? EMPTY;
  return {
    ...r,
    isStaff: r.isAdmin || r.isDeveloper || r.isSuperAdmin,
    loading: !!user && isLoading,
  };
}
